'use client';

import { Box } from '@mui/material';
import { alpha, ThemeProvider } from '@mui/material/styles';
import { ReactNode } from 'react';

import { createSiteTheme } from '@/theme';

const brokerTheme = createSiteTheme(true);
const { palette } = brokerTheme;

// CMS background variants that render as a solid blue / navy band on the broker page.
const darkBands = [
  'gradient-primary-dark',
  'purple-800',
  'purple-900',
  'primary',
  'primary-dark',
  'brand-dark',
];

const onDarkBand = (inner: string) =>
  darkBands.map((band) => `& .background-${band} ${inner}`).join(', ');

// Highlighted words in CMS headings (`<strong>` inside h1-h5) render as a pill.
const headingPill = ':is(h1, h2, h3, h4, h5) strong';

export function BrokerPageTheme({ children, enabled }: { children: ReactNode; enabled: boolean }) {
  if (!enabled) return <>{children}</>;

  return (
    <ThemeProvider theme={brokerTheme}>
      <Box
        sx={{
          color: 'text.primary',
          '& .orb-layer, & .orb-cover': { display: 'none' },
          // Tighten the transition from feature tabs into the growth section.
          '& [id$="-ComponentBlocksProductFeatures"]:has(+ [id$="-ComponentBlocksTwoColumnTextImageBlock"]) > [data-component^="Background-"]':
            {
              paddingBottom: { xs: 3, sm: 4 },
            },
          '& [id$="-ComponentBlocksProductFeatures"] + [id$="-ComponentBlocksTwoColumnTextImageBlock"] > [data-component^="Background-"]':
            {
              paddingTop: { xs: 3, sm: 4 },
            },
          '& [data-component="TwoColumnTextImageBlock"] > :first-child:has(+ [data-component="TwoColumnTextImageContent"])':
            {
              marginBottom: 3,
              '& :is(h1, h2, h3)': { marginBottom: '0 !important' },
            },
          // Partners site: white button with dark-blue text on blue bands.
          [onDarkBand('.MuiButton-contained:not(.Mui-disabled)')]: {
            background: palette.common.white,
            color: `${palette.brand.brandDark} !important`,
            '&:hover': { background: palette.purple[300] },
          },
          // Pills share the band colour, so render them as a translucent badge instead.
          [onDarkBand('.MuiTypography-pill')]: {
            background: alpha(palette.common.white, 0.16),
            color: palette.brand.primarySecondary1,
          },
          // Heading highlights: solid blue badge on light backgrounds ...
          [`& ${headingPill}`]: {
            WebkitTextFillColor: palette.common.white,
            '&::before': { background: palette.brand.primary, opacity: 1 },
          },
          // ... and a translucent badge with light-blue text on blue bands.
          [onDarkBand(headingPill)]: {
            WebkitTextFillColor: palette.brand.primarySecondary1,
            '&::before': { background: alpha(palette.common.white, 0.16), opacity: 1 },
          },
          // Review carousel cards (colours are hardcoded in embla.css for the main site).
          '& .embla__slide__number': {
            backgroundImage: `linear-gradient(${palette.purple[900]}, ${palette.purple[900]}), linear-gradient(to right, ${palette.purple[600]} 0%, ${palette.purple[600]} 100%)`,
          },
          '& .embla__slide.is-snapped .embla__slide__number': {
            backgroundImage: palette.gradients.reviewCard,
          },
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
}
